"use client";
import type { GameData } from "@/app/games/(details)/types";
import { usePassportProvider } from "@/context";
import { Action } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useWishlistHandler } from "@/hooks/useWishlistHandler";
import { formatDateOnly } from "@/utils/date";
import { formatGenreLabel } from "@/utils/string";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import Footer from "../Footer";
import { WindowsLogo } from "../icons/WindowsLogo";
import { BentoBox } from "./BentoBox/BentoBox";
import { FollowCTA } from "./FollowCTA";
import { GPOnboardingModal } from "./GPOnboarding/GPOnboardingModal";
import {
  GPExperimentType,
  getExperimentTypeFromSearchParams,
} from "./GPOnboarding/utils";
import { GlassButton } from "./GlassButton";
import { Quests } from "./RightPanel/Quests";
import { RightPanel } from "./RightPanel/RightPanel";
import { VideoIntro } from "./VideoIntro";
import { getPlatformButtons, getSocialButtons } from "./utils";

export const GamePageLayout = ({
  gameData,
}: {
  gameData: GameData;
}) => {
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const { newOnboardingFlow8BallNov25Gpt } = useFeatureFlags();

  const { isWishlisted } = useWishlistHandler(gameData.details.game_id);

  const initialViewedRef = useRef(false);

  const { track } = useGamePageAnalytics();

  const { isLoggedIn } = usePassportProvider();

  const searchParams = useSearchParams();
  const experimentType = getExperimentTypeFromSearchParams(searchParams);
  const showOnboardingOnLoadRef = useRef(
    searchParams?.get("showOnboarding") === "true" ||
      experimentType === GPExperimentType.EXTRINSIC ||
      experimentType === GPExperimentType.HYBRID ||
      experimentType === GPExperimentType.INGAME,
  );

  useEffect(() => {
    if (
      showOnboardingOnLoadRef.current &&
      !isLoggedIn &&
      newOnboardingFlow8BallNov25Gpt
    )
      setShowOnboardingModal(true);
  }, [isLoggedIn, newOnboardingFlow8BallNov25Gpt]);

  const closeOnboardingModal = useCallback(() => {
    setShowOnboardingModal(false);
    showOnboardingOnLoadRef.current = false;
  }, []);

  const openOnboardingModal = useCallback(() => {
    if (!newOnboardingFlow8BallNov25Gpt) return;
    setShowOnboardingModal(true);
  }, [newOnboardingFlow8BallNov25Gpt]);

  useEffect(() => {
    if (initialViewedRef.current) return;
    initialViewedRef.current = true;
    track({
      action: Action.VIEWED,
      extras: {
        control: "game-page",
      },
    });
  }, [track]);

  useEffect(() => {
    document.body.style.setProperty(
      "--accent-colour",
      gameData.gameContent?.accent_colour
        ?.replace("rgb(", "")
        .replace(")", "") || "0,255,242",
    );

    return () => {
      document.body.style.removeProperty("--accent-colour");
    };
  }, [gameData.gameContent?.accent_colour]);

  return (
    <>
      <GPOnboardingModal
        open={showOnboardingModal && newOnboardingFlow8BallNov25Gpt === true}
        onClose={closeOnboardingModal}
        experimentType={experimentType}
      />

      <VideoIntro gameData={gameData}>
        {/* Page Container */}
        <div className="min-h-screen flex flex-col flex-1 h-full justify-between px-[4vw] md:px-8 pb-52 md:pb-4 space-y-4">
          <div className="@container relative space-y-5 max-w-[108rem] flex flex-col items-center w-full mx-auto pt-10">
            {/* Header Container */}
            <div className="flex items-center justify-between w-full @3xl:gap-x-10">
              {/* Socials & Release/AMA Container */}
              <div className="space-y-5 flex flex-col items-start flex-1 max-w-[28rem] h-full justify-between relative">
                {/* Socials Glass Buttons */}
                <div className="@3xl:ml-2.5 flex flex-col items-center gap-y-2 @3xl:flex-row @3xl:gap-y-0 @3xl:gap-x-6 justify-start pb-16 @3xl:pb-0">
                  {/* TEMP TODO REMOVE @zaidarain1 hardcode for 8-ball, add to retool */}
                  {gameData.details.logo_url ? (
                    <GlassButton
                      target="_blank"
                      rel="noopener noreferrer"
                      label=""
                      subLabel=""
                      href={
                        gameData?.gameContent?.store_urls?.find((storeUrl) =>
                          storeUrl?.includes("store.steampowered.com"),
                        ) ?? gameData.details.landing_page_url
                      }
                      className="size-10 md:size-12"
                      labelsClassName="@3xl:block hidden"
                      control="site"
                      section={GamePageSection.TOP_BUTTONS}
                    >
                      <Image
                        src={gameData.details.logo_url}
                        alt="Site"
                        width={20}
                        height={20}
                        className="md:scale-100 scale-90"
                      />
                    </GlassButton>
                  ) : null}
                  {getSocialButtons(gameData.details.social_stats)}
                </div>
                <div className="@3xl:block hidden w-full h-px bg-white/10" />
                <div className="@3xl:ml-2.5 space-y-0.5 font-suisse absolute bottom-0 left-0 @3xl:static">
                  <p className="text-xs leading-none text-neutral-50/60">
                    Release Date
                  </p>
                  <p className="text-neutral-50 whitespace-nowrap @md:text-base text-sm">
                    {gameData.gameContent?.release_date
                      ? formatDateOnly(
                          new Date(gameData.gameContent.release_date),
                        )
                      : "N/A"}
                  </p>
                </div>
              </div>

              {/* Logo */}
              {gameData.details.logo_url ? (
                <Image
                  src={gameData.details.logo_url}
                  alt={`${gameData.details.name} Logo`}
                  width={466}
                  height={197}
                  className="object-contain max-w-[50%] @2xl:max-w-[40%] @3xl:max-w-[33%]"
                />
              ) : null}

              {/* Platforms & Categories Container */}
              <div className="space-y-5 flex flex-col items-end flex-1 max-w-[28rem] h-full justify-between relative">
                {/* Platforms Glass Buttons */}
                <div className="@3xl:mr-2.5 flex flex-col items-center gap-y-2 @3xl:flex-row @3xl:gap-y-0 @3xl:gap-x-6 justify-end pb-16 @3xl:pb-0">
                  {/* TEMP TODO REMOVE @zaidarain1 hardcode if no platforms, show windows */}
                  {!gameData.gameContent?.store_urls?.length ? (
                    <GlassButton
                      target="_blank"
                      rel="noopener noreferrer"
                      label="Windows"
                      control="windows"
                      section={GamePageSection.TOP_BUTTONS}
                      subLabel=""
                      href=""
                      className="backdrop-blur-0 bg-transparent size-fit"
                    >
                      <WindowsLogo className="size-5 scale-150" />
                    </GlassButton>
                  ) : (
                    getPlatformButtons(gameData.gameContent.store_urls)
                  )}
                </div>
                <div className="@3xl:block hidden w-full h-px bg-white/10" />
                <div className="@3xl:mr-2.5 space-y-0.5 font-suisse text-end absolute bottom-0 right-0 @3xl:static">
                  <p className="text-xs leading-none text-neutral-50/60">
                    Category
                  </p>
                  <p className="text-neutral-50 whitespace-nowrap @md:text-base text-sm">
                    {gameData.details?.genres
                      ?.map(formatGenreLabel)
                      .join(", ") ?? ""}
                  </p>
                </div>
              </div>
            </div>

            <FollowCTA
              gameData={gameData}
              showOnboardingModal={openOnboardingModal}
            />

            <BentoBox gameData={gameData} />

            {isWishlisted ? (
              <Quests
                gameData={gameData}
                className="md:hidden rounded-[1.25rem] glass-border relative pt-6 [&>div]:px-4 [&>li]:px-4 bg-black/30 backdrop-blur-[32px]"
              />
            ) : null}
          </div>
          <Footer className="!px-0" />
        </div>
        {/* Right Panel - Using flexbox, anchored to right edge */}
        <RightPanel
          className="h-screen sticky top-0 right-0 w-96"
          gameData={gameData}
          showOnboardingModal={openOnboardingModal}
        />
      </VideoIntro>
    </>
  );
};
